import React, { useEffect, useState } from "react";

const API_BASE_URL = "";

const emptyCard = {
  title: "",
  tagline: "",
  desc: "",
  image: "",
  buttonText: "",
  pagename: "",
};

const getMediaUrl = (path) => {
  if (!path) {
    return "";
  }

  if (path.startsWith("http")) {
    return path;
  }

  if (path.startsWith("/uploads/")) {
    return `${API_BASE_URL}${path}`;
  }

  return path;
};

const cloneCard = (card) => ({
  ...emptyCard,
  ...card,
});

const MostLovedExperiences = () => {
  const [data, setData] = useState({
    badge: "",
    mainTitle: "",
    accentTitle: "",
    subtitle: "",
    cards: [],
  });
  const [form, setForm] = useState(cloneCard(emptyCard));
  const [editorIndex, setEditorIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem("indiaboatsAdminToken");
  const isEditing = editorIndex !== null;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/most-loved-experiences`
        );

        if (!response.ok) {
          throw new Error("Failed to load Most Loved Experiences");
        }

        const result = await response.json();
        setData(result);
      } catch (error) {
        console.error("Most Loved Experiences fetch error:", error);
        alert("Failed to load section data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSectionChange = (field, value) => {
    setData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleFormChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleImageUpload = async (file) => {
    if (!file) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Image upload failed");
      }

      handleFormChange("image", result.fileUrl);
    } catch (error) {
      console.error("Image upload error:", error);
      alert(error.message || "Failed to upload image");
    }
  };

  const startAdd = () => {
    setEditorIndex(null);
    setForm(cloneCard(emptyCard));
  };

  const startEdit = (index) => {
    setEditorIndex(index);
    setForm(cloneCard(data.cards[index]));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applyFormToCards = (cards) => {
    const nextCards = [...cards];

    if (isEditing) {
      nextCards[editorIndex] = cloneCard(form);
      return nextCards;
    }

    const hasContent = Boolean(
      form.title || form.image || form.tagline || form.desc
    );

    if (hasContent) {
      nextCards.push(cloneCard(form));
    }

    return nextCards;
  };

  const persist = async (nextData, successMessage) => {
    const response = await fetch(
      `${API_BASE_URL}/api/most-loved-experiences`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(nextData),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || "Failed to save changes");
    }

    setData(result.data);
    alert(successMessage);
    return result.data;
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const nextData = {
        ...data,
        cards: applyFormToCards(data.cards || []),
      };

      const saved = await persist(
        nextData,
        "Most Loved Experiences updated successfully"
      );

      if (!isEditing) {
        const addedNew = (saved.cards || []).length > (data.cards || []).length;
        if (addedNew) {
          startAdd();
        }
      } else {
        setForm(cloneCard((saved.cards || [])[editorIndex] || emptyCard));
      }
    } catch (error) {
      console.error("Save error:", error);
      alert(error.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (index) => {
    const item = data.cards[index];
    const label = item?.title || `Experience ${index + 1}`;

    if (
      !window.confirm(
        `Delete "${label}"? This will remove it from the website.`
      )
    ) {
      return;
    }

    try {
      setSaving(true);

      const nextCards = (data.cards || []).filter(
        (_, itemIndex) => itemIndex !== index
      );

      await persist(
        { ...data, cards: nextCards },
        "Experience deleted successfully"
      );

      if (editorIndex === index) {
        startAdd();
      } else if (editorIndex !== null && editorIndex > index) {
        setEditorIndex(editorIndex - 1);
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert(error.message || "Failed to delete experience");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cms-page">
        <div className="cms-page-loading">Loading...</div>
      </div>
    );
  }

  const cards = data.cards || [];

  return (
    <div className="cms-page">
      <div className="cms-page-header">
        <div>
          <h1>Most Loved Experiences</h1>
          <p>
            Add a new experience or update an existing one from the list below.
          </p>
        </div>

        <button
          type="button"
          className="cms-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>Section Content</h2>
          <p>Edit the heading and description shown above the experience cards.</p>
        </div>

        <div className="cms-form-grid">
          <div className="cms-form-group">
            <label>Badge Text</label>
            <input
              type="text"
              value={data.badge || ""}
              onChange={(e) => handleSectionChange("badge", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Main Heading</label>
            <input
              type="text"
              value={data.mainTitle || ""}
              onChange={(e) => handleSectionChange("mainTitle", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Accent Heading</label>
            <input
              type="text"
              value={data.accentTitle || ""}
              onChange={(e) =>
                handleSectionChange("accentTitle", e.target.value)
              }
            />
          </div>

          <div className="cms-form-group cms-form-full">
            <label>Subtitle</label>
            <textarea
              rows="4"
              value={data.subtitle || ""}
              onChange={(e) => handleSectionChange("subtitle", e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>{isEditing ? "Update Experience" : "Add Experience"}</h2>
          <p>
            {isEditing
              ? `Editing experience ${editorIndex + 1}. Save changes to update it.`
              : "Fill the form and save to add a new experience card."}
          </p>
        </div>

        <div className="cms-editor-toolbar">
          <button type="button" className="cms-upload-btn" onClick={startAdd}>
            Add New
          </button>
        </div>

        <div className="cms-form-grid">
        <div className="cms-form-group">
          <label>Card Image</label>
          <div className="cms-upload-box">
            <div className="cms-upload-preview">
              {form.image ? (
                <img src={getMediaUrl(form.image)} alt={form.title} />
              ) : (
                <span>No image selected</span>
              )}
            </div>
            <div className="cms-upload-actions">
              <input
                type="file"
                id="most-loved-image-editor"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                className="cms-file-input"
                onChange={(e) => handleImageUpload(e.target.files[0])}
              />
              <label htmlFor="most-loved-image-editor" className="cms-upload-btn">
                Choose Image
              </label>
            </div>
          </div>
          <small>JPG, JPEG, PNG or WEBP.</small>
        </div>

        <div className="cms-form-group">
          <label>Title</label>
          <input
            type="text"
            value={form.title || ""}
            onChange={(e) => handleFormChange("title", e.target.value)}
          />
        </div>

        <div className="cms-form-group">
          <label>Tagline</label>
          <textarea
            rows="3"
            value={form.tagline || ""}
            onChange={(e) => handleFormChange("tagline", e.target.value)}
          />
        </div>

        <div className="cms-form-group">
          <label>Description</label>
          <textarea
            rows="2"
            value={form.desc || ""}
            onChange={(e) => handleFormChange("desc", e.target.value)}
          />
        </div>

        <div className="cms-form-group">
          <label>Button Text</label>
          <input
            type="text"
            value={form.buttonText || ""}
            onChange={(e) => handleFormChange("buttonText", e.target.value)}
          />
        </div>

        <div className="cms-form-group">
          <label>Button URL</label>
          <input
            type="text"
            value={form.pagename || ""}
            onChange={(e) => handleFormChange("pagename", e.target.value)}
          />
          <small>
            Example: birthday-anniversary-yacht-celebration-mumbai
          </small>
        </div>
        </div>

        <div className="cms-bottom-actions">
          <button
            type="button"
            className="cms-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>Existing Experiences</h2>
          <p>Update fills the form above. Delete asks for confirmation first.</p>
        </div>

        {cards.length === 0 ? (
          <p className="cms-inquiries-empty">No experience cards added yet.</p>
        ) : (
          <div className="cms-item-list">
            {cards.map((card, index) => (
              <div
                className={`cms-item-row${
                  editorIndex === index ? " is-active" : ""
                }`}
                key={index}
              >
                <div className="cms-item-row-thumb">
                  {card.image ? (
                    <img
                      src={getMediaUrl(card.image)}
                      alt={card.title || "Experience"}
                    />
                  ) : (
                    <span>No image</span>
                  )}
                </div>
                <div className="cms-item-row-info">
                  <strong>{card.title || `Experience ${index + 1}`}</strong>
                  <span>{card.tagline || "No tagline"}</span>
                </div>
                <div className="cms-item-row-actions">
                  <button
                    type="button"
                    className="cms-upload-btn"
                    onClick={() => startEdit(index)}
                  >
                    Update
                  </button>
                  <button
                    type="button"
                    className="cms-delete-btn"
                    onClick={() => handleDelete(index)}
                    disabled={saving}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MostLovedExperiences;
